import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

const reveal = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
};

export default function Thesis() {
    const { t } = useT();
    const { verticals, lookingFor, moat } = t.thesis;

    return (
        <section
            id="thesis"
            data-testid="thesis-section"
            className="relative py-24 md:py-32 border-t border-[rgba(201,169,110,0.10)]"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-12">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-6">
                    {t.thesis.label}
                </p>
                <h2 className="font-display text-[28px] md:text-[42px] font-medium leading-[1.1] tracking-[-0.02em] max-w-[820px] mb-16">
                    {t.thesis.headlineBefore}{" "}
                    <span className="text-[#C9A96E]">
                        {t.thesis.headlineAccent}
                    </span>{" "}
                    {t.thesis.headlineAfter}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={reveal}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        data-testid="thesis-card-verticals"
                        className="p-8 md:p-10 border border-[rgba(201,169,110,0.15)] rounded-[4px] hover:border-[rgba(201,169,110,0.35)] transition-colors"
                    >
                        <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] mb-6 font-medium">
                            {t.thesis.verticalsLabel}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-3 text-[17px] md:text-[19px] leading-[1.5] text-[#E8E6DF]">
                            {verticals.map((v, i) => (
                                <span
                                    key={v}
                                    className="flex items-center gap-3"
                                >
                                    {v}
                                    {i < verticals.length - 1 && (
                                        <span className="text-[#C9A96E]/50 text-[14px]">
                                            ·
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={reveal}
                        transition={{
                            duration: 0.6,
                            delay: 0.1,
                            ease: "easeOut",
                        }}
                        data-testid="thesis-card-looking-for"
                        className="p-8 md:p-10 border border-[rgba(201,169,110,0.15)] rounded-[4px] hover:border-[rgba(201,169,110,0.35)] transition-colors"
                    >
                        <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] mb-6 font-medium">
                            {t.thesis.lookingForLabel}
                        </h3>
                        <ul className="space-y-4">
                            {lookingFor.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-4 text-[17px] md:text-[19px] leading-[1.5] text-[#E8E6DF]"
                                >
                                    <svg
                                        className="mt-[10px] text-[#C9A96E] shrink-0"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 6.5l2.5 2.5L10 3"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={reveal}
                        transition={{
                            duration: 0.6,
                            delay: 0.2,
                            ease: "easeOut",
                        }}
                        data-testid="thesis-card-moat"
                        className="md:col-span-2 p-8 md:p-12 border border-[rgba(201,169,110,0.15)] rounded-[4px] hover:border-[rgba(201,169,110,0.35)] transition-colors"
                    >
                        <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] mb-8 font-medium">
                            {t.thesis.moatLabel}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                            {moat.map((m) => (
                                <div key={m.title}>
                                    <p className="font-display text-[18px] md:text-[20px] font-medium tracking-[-0.01em] text-[#E8E6DF] mb-3">
                                        {m.title}
                                    </p>
                                    <p className="text-[14px] leading-[1.7] text-[rgba(232,230,223,0.55)]">
                                        {m.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
